import React, { useMemo } from 'react';
import { Box, Paper, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslatedMessages } from '../../../../hooks/useTranslatedMessages';

const VatCategoryBarChart = ({ categories = [], loading = false }) => {
  const theme = useTheme();
  const { misDashboard: m } = useTranslatedMessages();

  const chartData = useMemo(
    () =>
      (categories ?? []).map((item) => ({
        category: item.label || item.category,
        sales: item.sales ?? 0,
        purchases: item.purchases ?? 0,
      })),
    [categories],
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
        {m.vat?.category_title ?? 'Sales & Purchase by VAT Category'}
      </Typography>

      {loading ? (
        <Skeleton variant="rounded" height={320} />
      ) : !chartData.length ? (
        <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {m.vat?.no_data ?? 'No VAT data available for the selected filters.'}
          </Typography>
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis dataKey="category" stroke={theme.palette.text.secondary} style={{ fontSize: 11 }} />
            <YAxis
              stroke={theme.palette.text.secondary}
              style={{ fontSize: 11 }}
              tickFormatter={(value) => `${Math.round(value / 1000)}k`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
                color: theme.palette.text.primary,
              }}
              formatter={(value) => `AED ${Number(value).toLocaleString()}`}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar
              dataKey="sales"
              fill={theme.palette.primary.main}
              radius={[6, 6, 0, 0]}
              name={m.vat?.sales ?? 'Sales'}
            />
            <Bar
              dataKey="purchases"
              fill={theme.palette.success.main}
              radius={[6, 6, 0, 0]}
              name={m.vat?.purchases ?? 'Purchase'}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
};

export default VatCategoryBarChart;
